import React, { useEffect, useMemo, useRef, useState } from 'react';

/**
 * Searchable/typeable dropdown, drop-in replacement for a native <select>.
 * - the list only appears once at least one character has been typed
 *   (a blank/empty field shows nothing, even when focused)
 * - typing filters options live (client-side)
 * - ArrowDown/ArrowUp move the highlight; Enter selects it
 * - Tab selects the highlighted option (or first match) before moving focus
 * - blur closes the list
 * - onChange receives the option's underlying value (unchanged from <select>)
 *
 * options: [{ value, label }]
 */
export default function Combobox({
  label,
  value,
  onChange,
  options = [],
  placeholder = '-- select --',
  required,
  error,
  disabled,
}) {
  const [focused, setFocused] = useState(false);
  const [query, setQuery] = useState('');
  const [showAll, setShowAll] = useState(false); // set by the dropdown-trigger icon
  const [highlight, setHighlight] = useState(0);
  const rootRef = useRef();
  const inputRef = useRef();
  const itemRefs = useRef([]);

  const selected = options.find((o) => String(o.value) === String(value));
  const selectedLabel = selected ? selected.label : '';

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    // Blank query: show the full list only when opened via the trigger icon.
    if (!q) return showAll ? options : [];
    return options.filter((o) => String(o.label).toLowerCase().includes(q));
  }, [options, query, showAll]);

  // The list shows once something is typed, or when opened via the trigger icon.
  const showList = focused && (query.trim().length > 0 || showAll);

  // Keep the highlighted item in view during keyboard navigation.
  useEffect(() => {
    if (showList) itemRefs.current[highlight]?.scrollIntoView({ block: 'nearest' });
  }, [highlight, showList]);

  // Fallback: close on outside click.
  useEffect(() => {
    const onDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setFocused(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  const close = () => {
    setFocused(false);
    setQuery('');
    setShowAll(false);
  };

  // Dropdown-trigger icon: open the full, unfiltered list regardless of input.
  const openFullList = () => {
    if (disabled) return;
    setFocused(true);
    setQuery('');
    setShowAll(true);
    const idx = options.findIndex((o) => String(o.value) === String(value));
    setHighlight(idx >= 0 ? idx : 0);
    inputRef.current?.focus();
  };

  const select = (opt) => {
    if (opt) onChange(opt.value);
    close();
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      if (!showList) return;
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      if (!showList) return;
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      if (showList && filtered[highlight]) {
        e.preventDefault();
        select(filtered[highlight]);
      }
    } else if (e.key === 'Tab') {
      // Select the highlighted option (if any) before focus leaves the field.
      if (showList && filtered.length > 0) select(filtered[highlight]);
      else close();
    } else if (e.key === 'Escape') {
      close();
    }
  };

  return (
    <label className="block" ref={rootRef}>
      {label && (
        <span className="mb-1 block text-sm font-medium text-gray-700">
          {label}
          {required && <span className="text-red-500"> *</span>}
        </span>
      )}
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          disabled={disabled}
          // While focused the field shows the typed query (blank until typing);
          // when unfocused it shows the current selection's label.
          value={focused ? query : selectedLabel}
          placeholder={placeholder}
          onFocus={() => {
            setFocused(true);
            setQuery('');
            setHighlight(0);
          }}
          onBlur={close}
          onChange={(e) => {
            setQuery(e.target.value);
            setShowAll(false); // typing returns to filtered mode
            setHighlight(0);
          }}
          onKeyDown={onKeyDown}
          className={`w-full rounded-md border bg-white px-3 py-2 pr-8 text-sm shadow-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand disabled:bg-gray-100 ${
            error ? 'border-red-400' : 'border-gray-300'
          }`}
        />
        <button
          type="button"
          tabIndex={-1}
          disabled={disabled}
          // mousedown (not click) fires before the input blur, so the list opens
          // rather than being closed by the blur.
          onMouseDown={(e) => {
            e.preventDefault();
            if (showAll) close();
            else openFullList();
          }}
          className="absolute right-1 top-1/2 -translate-y-1/2 px-1 text-gray-400 hover:text-brand"
          aria-label="Show all options"
        >
          ▾
        </button>

        {showList && (
          <ul className="absolute z-30 mt-1 max-h-56 w-full overflow-y-auto rounded-md border border-gray-200 bg-white py-1 text-sm shadow-lg">
            {filtered.length === 0 ? (
              <li className="px-3 py-2 text-gray-400">No matches</li>
            ) : (
              filtered.map((o, i) => (
                <li
                  key={o.value}
                  ref={(el) => (itemRefs.current[i] = el)}
                  // mousedown (not click) fires before the input's blur, so the
                  // selection registers before the list would otherwise close.
                  onMouseDown={(e) => {
                    e.preventDefault();
                    select(o);
                  }}
                  onMouseEnter={() => setHighlight(i)}
                  className={`cursor-pointer px-3 py-2 ${
                    i === highlight ? 'bg-brand-light text-brand-dark' : 'text-gray-700'
                  } ${String(o.value) === String(value) ? 'font-medium' : ''}`}
                >
                  {o.label}
                </li>
              ))
            )}
          </ul>
        )}
      </div>
      {error && <span className="mt-1 block text-xs text-red-600">{error}</span>}
    </label>
  );
}
